import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  CircularProgress,
  Box,
  Typography,
  Alert,
  Fade,
  Zoom,
  Divider,
} from "@mui/material";

import {
  Publish,
  Unpublished,
  Close,
  School,
} from "@mui/icons-material";


import api from "../api/api";
import { useAcademicYear } from "../AcademicYearContext";


const MAROON = "#800000";
const BLACK = "#111111";
const GRAY = "#f1f1f1";


const AdminPublishGradesPopup = ({ open, onClose }) => {

  const { selectedYear } = useAcademicYear();

  const [periods, setPeriods] = useState([]);
  const [selectedPeriod, setSelectedPeriod] = useState("");
  const [loading, setLoading] = useState(false);
  const [processing, setProcessing] = useState(false);

  const token = localStorage.getItem("access");
  const [message, setMessage] = useState({ type: "", text: "" });

  const showMessage = (type, text) => {
  setMessage({ type, text });

  setTimeout(() => {
    setMessage({
      type: "",
      text: "",
    });
  }, 5000);
};

useEffect(() => {

  if (!open) return;

  const fetchPeriods = async () => {

    setLoading(true);

    try {

      const response = await api.get(
        "/api/users/periods/",
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      setPeriods(response.data);

    } catch (error) {

      showMessage(
        "error",
        "Failed to fetch periods."
      );

    } finally {

      setLoading(false);

    }

  };

  fetchPeriods();

}, [open, token]);



const publishGrades = async () => {

  if (!selectedPeriod) {
    showMessage(
      "error",
      "Please select a period."
    );
    return;
  }

  setProcessing(true);

  try {

    const response = await api.post(
      "/api/users/student-averages/publish-grades/",
{
  period: selectedPeriod,
  academic_year: selectedYear?.id,
},
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );

    showMessage(
      "success",
      response.data.message
    );

  } catch (error) {

    showMessage(
      "error",
      error.response?.data?.error ||
      "Failed to publish grades."
    );

  } finally {

    setProcessing(false);

  }

};

const unpublishGrades = async () => {

  if (!selectedPeriod) {
    showMessage(
      "error",
      "Please select a period."
    );
    return;
  }

  setProcessing(true);

  try {

    const response = await api.post(
      "/api/users/student-averages/unpublish-grades/",
{
  period: selectedPeriod,
  academic_year: selectedYear?.id,
},
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );

    showMessage(
      "success",
      response.data.message
    );

  } catch (error) {

    showMessage(
      "error",
      error.response?.data?.error ||
      "Failed to unpublish grades."
    );

  } finally {

    setProcessing(false);

  }

};



return (

<Dialog

open={open}

onClose={onClose}

TransitionComponent={Zoom}

fullWidth

maxWidth="sm"

PaperProps={{
sx:{
borderRadius:5,
background:
"linear-gradient(145deg,#ffffff,#eeeeee)",
boxShadow:
"0 20px 60px rgba(0,0,0,.4)"
}
}}

>


<Fade in={open}>


<Box>


<DialogTitle

sx={{
background:
`linear-gradient(135deg,${MAROON},${BLACK})`,
color:"#fff",
textAlign:"center",
py:3
}}

>


<School sx={{fontSize:40}}/>

<Typography

variant="h5"

fontWeight="bold"

>

Grade Publication Control

</Typography>


</DialogTitle>



<DialogContent sx={{p:4}}>



{selectedYear ? (

<Alert

severity="info"

sx={{
mb:3,
borderRadius:3
}}

>

Academic Year:

<strong>
&nbsp;{selectedYear.name}
</strong>

</Alert>


):(


<Alert severity="warning">

No academic year selected.

</Alert>


)}




<FormControl fullWidth sx={{mt:2}}>


<InputLabel>
Select Period
</InputLabel>


<Select

value={selectedPeriod}

label="Select Period"

onChange={(e)=>
setSelectedPeriod(e.target.value)
}

sx={{

background:GRAY,

borderRadius:2

}}

>


{

loading ?

<MenuItem disabled>

<CircularProgress size={22}/>

</MenuItem>


:


periods.length ?


periods.map(period=>(

<MenuItem

key={period.id}

value={period.id}

>

{period.name}

</MenuItem>

))


:


<MenuItem disabled>

No Period Found

</MenuItem>

}


</Select>


</FormControl>




<Divider sx={{my:3}}/>




<Box

display="flex"

gap={2}

flexDirection={{
xs:"column",
sm:"row"
}}

>


<Button

fullWidth

variant="contained"

startIcon={<Publish/>}

onClick={publishGrades}

disabled={
processing ||
!selectedPeriod ||
!selectedYear
}


sx={{

background:MAROON,

borderRadius:3,

py:1.4,

fontWeight:"bold",

"&:hover":{
background:"#5b0000"
}

}}

>


{

processing ?

<CircularProgress size={25} color="inherit"/>

:

"Publish"

}


</Button>





<Button

fullWidth

variant="contained"

startIcon={<Unpublished/>}

onClick={unpublishGrades}

disabled={
processing ||
!selectedPeriod ||
!selectedYear
}


sx={{

background:BLACK,

borderRadius:3,

py:1.4,

fontWeight:"bold",

"&:hover":{
background:"#333"
}

}}

>


{

processing ?

<CircularProgress size={25} color="inherit"/>

:

"Unpublish"

}


</Button>



</Box>




<Button

fullWidth

variant="outlined"

startIcon={<Close/>}

onClick={onClose}

sx={{

mt:3,

borderColor:MAROON,

color:MAROON,

borderRadius:3

}}

>

Close

</Button>




</DialogContent>

{message.text && (
  <Fade in={true}>
    <Alert
      severity={message.type === "success" ? "success" : "error"}
      sx={{
        mb: 3,
        borderRadius: 3,
        fontWeight: "bold",
        fontSize: 15,
        alignItems: "center",
        boxShadow: "0 8px 18px rgba(0,0,0,.15)",
        ...(message.type === "success"
          ? {
              background: "#dff6ff",
              color: "#0b4f6c",
              border: "1px solid #8fd3ff",
              "& .MuiAlert-icon": {
                color: "#0b4f6c",
              },
            }
          : {
              background: "#8B0000",
              color: "#fff",
              border: "1px solid #600000",
              "& .MuiAlert-icon": {
                color: "#fff",
              },
            }),
      }}
    >
      {message.text}
    </Alert>
  </Fade>
)}


</Box>


</Fade>


</Dialog>

);


};


export default AdminPublishGradesPopup;